import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Trash2, 
  UserCheck, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Building,
  Phone,
  Mail,
  MapPin,
  Star,
  TrendingUp,
  Users,
  DollarSign,
  FileText,
  ArrowLeft,
  Settings,
  ChevronUp,
  ChevronDown,
  MoreHorizontal,
  RefreshCw,
  HelpCircle
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import ConfirmationModal from '../common/ConfirmationModal';
import SubcontractingService from '../../services/subcontractingService';

// Custom Dropdown Component - exactly like Task Management
const ActionDropdown = ({ subcontractor, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setOpenUpward(window.innerHeight - rect.bottom < 120);
    }
    setIsOpen(prev => !prev);
  };

  return (
    <div
      ref={dropdownRef}
      className="relative"
      style={{ zIndex: isOpen ? 10000 : 'auto' }}
    >
      {/* Action Button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
      >
        <MoreHorizontal size={16} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${window.innerHeight -
                  buttonRef.current.getBoundingClientRect().top +
                  8}px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            left: `${buttonRef.current.getBoundingClientRect().left}px`,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                onClick={() => { setIsOpen(false); onView(subcontractor); }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
              >
                <Eye size={16} className="mr-2.5 text-gray-500" />
                View
              </button>
            </li>

            <li>
              <button
                onClick={() => { setIsOpen(false); onEdit(subcontractor); }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
              >
                <Edit size={16} className="mr-2.5 text-gray-500" />
                Edit
              </button>
            </li>

            <li>
              <button
                onClick={() => { setIsOpen(false); onDelete(subcontractor); }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
              >
                <Trash2 size={16} className="mr-2.5 text-red-500" />
                Delete
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const SubcontractorManagement = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [subcontractors, setSubcontractors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [subcontractorToDelete, setSubcontractorToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    itemsPerPage: 10,
    totalItems: 0,
    current: 1,
    pages: 1,
    limit: 10
  });

  // Transform subcontracting data to subcontractor format
  const transformSubcontractingToSubcontractor = (subcontracting) => {
    // Get performance rating (default to 3 if not available)
    const rating = subcontracting.performance?.overallRating || 3;
    
    // Determine performance level based on rating
    const getPerformanceLevel = (rating) => {
      if (rating >= 4.5) return 'Excellent';
      if (rating >= 3.5) return 'Good';
      if (rating >= 2.5) return 'Average';
      return 'Poor';
    };

    // Get location from address
    const location = subcontracting.addresses?.headquarters 
      ? `${subcontracting.addresses.headquarters.city || ''}, ${subcontracting.addresses.headquarters.state || ''}`.trim()
      : '';

    // Get certifications
    const certifications = subcontracting.compliance?.certifications?.map(cert => cert.name) || [];

    // Calculate total value (placeholder - would need to aggregate from projects/contracts)
    const totalValue = 0; // This would need to be calculated from actual project/contract data

    const availability = subcontracting.metadata?.availability;

    const derivedStatus = (() => {
      if (availability === 'available') return 'Active';
      if (availability === 'busy') return 'Pending';
      if (availability === 'unavailable') return 'Inactive';
      return subcontracting.status || 'Active';
    })();

    return {
      id: subcontracting.subcontractorId || subcontracting._id,
      _id: subcontracting._id,
      name: subcontracting.name || '',
      contactPerson: subcontracting.primaryContact?.name || '',
      email: subcontracting.primaryContact?.email || subcontracting.communication?.email || '',
      phone: subcontracting.primaryContact?.phone || subcontracting.communication?.phone || '',
      category: subcontracting.industry || subcontracting.sector || 'Other',
      status: derivedStatus,
      rating: rating,
      performance: getPerformanceLevel(rating),
      totalValue: totalValue,
      location: location,
      certifications: certifications,
      subcontractingData: subcontracting // Keep full subcontracting data for reference
    };
  };

  useEffect(() => {
    loadSubcontractors();
  }, [pagination.page, searchTerm, statusFilter, categoryFilter]);

  // Debounce search
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (pagination.page === 1) {
        loadSubcontractors();
      } else {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const loadSubcontractors = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page || 1,
        limit: pagination.itemsPerPage || 10,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(categoryFilter && { industry: categoryFilter })
      };

      const response = await SubcontractingService.getSubcontractings(params);
      
      if (response && response.success) {
        // Transform subcontracting data to subcontractor format
        const transformedSubcontractors = (response.data || []).map(transformSubcontractingToSubcontractor);
        setSubcontractors(transformedSubcontractors);
        
        // Update pagination from response
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            current: response.pagination.current || response.pagination.page || 1,
            pages: response.pagination.pages || 1,
            totalItems: response.pagination.total || 0,
            limit: response.pagination.limit || 10
          }));
        }
      } else {
        setSubcontractors([]);
        // Only show error if it's not an empty result
        if (response && !response.data) {
          showError('Failed to load subcontractors.');
        }
      }
    } catch (err) {
      console.error('Error loading subcontractors:', err);
      const errorMessage = err?.message || err?.response?.data?.message || 'Failed to load subcontractors.';
      showError(errorMessage);
      setSubcontractors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadSubcontractors();
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/subcontractor-management';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)'
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh subcontractors list',
      disabled: loading
    },
    {
      label: 'Add Subcontractor',
      icon: <Plus size={18} />,
      onClick: () => navigate('/subcontracting/management/create'),
      variant: 'primary',
      tooltip: 'Add a new subcontractor'
    }
  ];

  const handleView = (subcontractor) => {
    const subcontractorId = subcontractor._id || subcontractor.id;
    if (subcontractorId) {
      navigate(`/subcontracting/management/view/${subcontractorId}`);
    } else {
      console.error('Subcontractor ID not found:', subcontractor);
      showError('Unable to view subcontractor: ID not found');
    }
  };

  const handleEdit = (subcontractor) => {
    const subcontractorId = subcontractor._id || subcontractor.id;
    if (subcontractorId) {
      navigate(`/subcontracting/management/edit/${subcontractorId}`);
    } else {
      console.error('Subcontractor ID not found:', subcontractor);
      showError('Unable to edit subcontractor: ID not found');
    }
  };

  const handleDelete = (subcontractor) => {
    console.log('handleDelete called with:', subcontractor);
    if (!subcontractor) {
      console.error('No subcontractor provided to handleDelete');
      return;
    }
    setSubcontractorToDelete(subcontractor);
    setShowDeleteModal(true);
    console.log('Delete modal should now be open');
  };

  const confirmDelete = async () => {
    if (!subcontractorToDelete) return;
    
    setIsDeleting(true);
    try {
      const subcontractorId = subcontractorToDelete._id || subcontractorToDelete.id;
      if (!subcontractorId) {
        throw new Error('Subcontractor ID not found');
      }
      
      await SubcontractingService.deleteSubcontracting(subcontractorId);
      success('Subcontractor deleted successfully!');
      // Reload the list
      await loadSubcontractors();
      setShowDeleteModal(false);
      setSubcontractorToDelete(null);
    } catch (err) {
      console.error('Error deleting subcontractor:', err);
      const errorMessage = err?.message || err?.response?.data?.message || 'Failed to delete subcontractor.';
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const getSubcontractorStatusBadge = (status) => {
    const statusConfig = {
    Active: { color: '#10b981', bg: '#d1fae5', text: 'Active' },
    Pending: { color: '#f59e0b', bg: '#fef3c7', text: 'Pending' },
    Inactive: { color: '#6b7280', bg: '#f3f4f6', text: 'Inactive' },
    Suspended: { color: '#ef4444', bg: '#fee2e2', text: 'Suspended' },
    };
    
  const config = statusConfig[status] || statusConfig.Active;

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
      style={{
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.color}20`,
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: config.color }}
      />
      {config.text}
    </span>
  );
  };


  const getPerformanceBadge = (performance) => {
    const performanceConfig = {
    Excellent: { color: '#10b981', bg: '#d1fae5' },
    Good: { color: '#3b82f6', bg: '#dbeafe' },
    Average: { color: '#f59e0b', bg: '#fef3c7' },
    Poor: { color: '#ef4444', bg: '#fee2e2' },
    };
    
  const config = performanceConfig[performance] || performanceConfig.Average;

  return (
    <span
      className="inline-block px-1.5 py-0.5 text-xs font-medium rounded"
      style={{
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.color}20`,
      }}
    >
      {performance}
    </span>
  );
  };

  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'name',
      label: 'Subcontractor',
      render: (value, subcontractor) => (
        <div>
          <div className="fw-bold text-truncate" style={{ maxWidth: '150px' }} title={subcontractor.name}>
            {subcontractor.name}
          </div>
          <small className="text-muted">{subcontractor.id}</small>
        </div>
      )
    },
    {
      key: 'contactPerson',
      label: 'Contact Person',
      render: (value, subcontractor) => (
        <div>
          <div className="fw-medium text-truncate" style={{ maxWidth: '120px' }} title={subcontractor.contactPerson}>
            {subcontractor.contactPerson}
          </div>
          <small className="text-muted text-truncate d-block" style={{ maxWidth: '120px' }} title={subcontractor.email}>
            {subcontractor.email}
          </small>
        </div>
      )
    },
    {
      key: 'category',
      label: 'Category',
      render: (value, subcontractor) => (
        <span className="badge bg-light text-dark text-truncate d-inline-block" style={{ maxWidth: '100px' }} title={subcontractor.category}>
          {subcontractor.category}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (_, subcontractor) =>
        getSubcontractorStatusBadge(subcontractor.status),
    },
    {
      key: 'rating',
      label: 'Rating',
      render: (value, subcontractor) => (
        <div className="d-flex align-items-center">
          <Star size={16} className="text-warning me-1" />
          <span className="fw-medium">{subcontractor.rating}</span>
        </div>
      )
    },
    {
      key: 'performance',
      label: 'Performance',
      render: (_, subcontractor) =>
        getPerformanceBadge(subcontractor.performance),
    },
    {
      key: 'totalValue',
      label: 'Total Value',
      render: (value, subcontractor) => (
        <span className="fw-medium text-truncate d-block" style={{ maxWidth: '100px' }} title={`$${subcontractor.totalValue.toLocaleString()}`}>
          ${subcontractor.totalValue.toLocaleString()}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, subcontractor) => {
        if (!subcontractor || !subcontractor.id) {
          console.error('Invalid subcontractor data in render function:', subcontractor);
          return <span style={{ color: 'red' }}>Error</span>;
        }
        return (
          <ActionDropdown 
            subcontractor={subcontractor}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        );
      }
    }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Subcontractor Management"
        description="Manage subcontractor profiles, certifications, and performance tracking"
        actions={headerActions}
      />
      <div className="page-container">
      {/* Subcontractors Table */}
      <DataTable
        data={subcontractors}
        columns={columns}
        title="Subcontractors"
        loading={loading}
        pagination={{
          currentPage: pagination.current || pagination.page || 1,
          totalPages: pagination.pages || 1,
          total: pagination.totalItems || 0,
          itemsPerPage: pagination.limit || pagination.itemsPerPage || 10
        }}
        onPageChange={(page) => {
          setPagination(prev => ({
            ...prev,
            page: page,
            current: page
          }));
        }}
        emptyMessage="No subcontractors found. Click 'Add Subcontractor' to get started."
        showActions={false}
        searchPlaceholder="Search subcontractors..."
        searchable={true}
      />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Subcontractor"
        message={`Are you sure you want to delete "${subcontractorToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default SubcontractorManagement;
